/*
  React 16 depends on the collection types Map and Set. If you support older
  browsers and devices which may not yet provide these natively (e.g. IE < 11),
  consider including a global polyfill in your bundled application, such as
  core-js or babel-polyfill.
*/

import '@babel/polyfill';

/*
  React 16 also depends on requestAnimationFrame (even in test environments).
*/
import './lib/core/requestAnimationFramePolyfill';
import './lib/core/cancelAnimationFramePolyfill';

/*
  Isomorphic WHATWG Fetch API, for Node & Browserify
  https://github.com/matthew-andrews/isomorphic-fetch
*/
import 'isomorphic-fetch';

/*
  trimLeft and trimRight are used by react-konva
*/
import 'core-js/es/string';

// -------------------------------------------------------

import * as Sentry from '@sentry/react';
import React from 'react';
import { render } from 'react-dom';

// Must be the first local import !
// Because it set the client/target
import './client';

import pkg from '../package.json';

import { setWebSocketIsConnected } from './actions/runtime';
import Client, { Target } from './client';
import { IgnoredErrors, IgnoredUrls } from './constants/sentry';
import createHistory from './createHistory';
import createSocket from './createSocket';
import { getAppDOMElement } from './DOMElements';
import installBridge, { installNativeHandler } from './lib/bridge';
import { createAuthManager } from './managers/authManager';
import Root from './root';
import configureStore from './store';
import dispatcher from './utils/dispatcher';
import handleEmbeddedNotification, { handleInternalNavigation } from './utils/notification';
import saveSocketOnStorage from './utils/saveSocketOnStorage';

// Init Sentry
if (Target.__ENV__ === 'production') {
  const sentryId = `${pkg.version}.${process.env.REACT_APP_BUILD_ID}`;

  Sentry.init({
    dsn: 'https://92006269191640f88476fd10d045f538@sentry.io/1731981',
    release: `winabet@${sentryId}`,
    environment: process.env.REACT_APP_BUILD_ENV,
    sampleRate: 0.05,
    ignoreErrors: IgnoredErrors,
    denyUrls: IgnoredUrls,
    // Dist is needed in order to link sourceMaps and sourceCode
    // Should be the same value in the Event JSON file and in the artifacts dist tag
    dist: sentryId,
  });

  Client.tracker && Sentry.setTag('oid', Client.tracker);
  Client.store && Sentry.setTag('store', Client.store);
  Client.storeId && Sentry.setTag('storeId', Client.storeId);
  Client.application && Sentry.setTag('application', Client.application);
  Client.platform && Sentry.setTag('platform', Client.platform);
}

// Install native bridge
installBridge();

const history = createHistory();

// Initialize socket
const suggestedSocket = {
  host: global.BETTING_CONFIGURATION?.SOCKET_IO_SERVER || null,
  path: global.BETTING_CONFIGURATION?.SOCKET_IO_PATH || null,
};
saveSocketOnStorage(suggestedSocket);

const socket = createSocket();

// Initialize redux store
const store = configureStore(socket, history, undefined);

createAuthManager(store.dispatch);

// dispatch APP_INIT
store.dispatch({ type: '@@APP_INIT' });
socket.on('connect', () => {
  store.dispatch(setWebSocketIsConnected(socket.isConnected()));
});
// Expose the store only in BUILD_ENV !== prod
if (process.env.REACT_APP_BUILD_ENV !== 'prod') {
  window.__store = store;
}

dispatcher(store, window.PRELOADED_STATE || {}, history);

handleEmbeddedNotification(history, store.dispatch);

// On injecte dans la window la fonction pour naviguer dans le betting
window.navigateBetting = (uri: string) => handleInternalNavigation({ history, dispatch: store.dispatch, uri });

// On inject le handler pour les event natif
installNativeHandler(store);

render(<Root store={store} history={history} />, getAppDOMElement());

if (module.hot) {
  module.hot.accept('./root', () => {
    const NextRoot = require('./root').default;
    render(<NextRoot store={store} history={history} />, getAppDOMElement());
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// and uncomment this line
// reportWebVitals();
